// The lab week: what a sample week looks like, built from real ingested data.
//
// WHY THIS EXISTS. A live-like test page on real data, sampling a week of
// managing a team — setting a lineup, then the week's results. It is the same
// need, and the same answer, as
// `/lab/player/:id` on 2026-09-13 — a development surface, not a screen.
//
// WHAT MAKES IT HONEST. There is no league here. No row is written, nothing is
// stored, and nobody owns these players. The two rosters are DERIVED on every
// request from the real directory and the real weekly stat lines, by a rule
// this file states out loud, so the page can say where every number came from.
//
//   NOTHING INVENTED — every point is the real scoring engine run over a real
//   nflverse stat line. A week with no lines yet reports `none` and shows no
//   numbers rather than zeroes, because a zero and an unknown look the same on
//   a screen and only one of them is true.
//
//   NOTHING ASSERTED — no sentence anywhere tells anybody who to play
//   (`tools-not-instructions`). The lineup is filled by a stated rule and the
//   rule is printed.
//
//   EVERYTHING TRACEABLE — `basis` says which weeks fed the ranking, `source`
//   says who published the lines, and `asOf` says when.

import { z } from 'zod';

/** One player on a roster, with what is known about them for this week. */
export const LabPlayer = z.object({
  id: z.string(),
  name: z.string(),
  /** Every position the directory lists; the first is the primary. */
  positions: z.array(z.string()),
  proTeam: z.string().nullable(),
  /**
   * Mean points per completed week BEFORE the week being shown, through the
   * league's scoring rules. `null` when this player has no completed week yet —
   * a rookie in week 1, or somebody who has not played.
   *
   * IT IS NOT A FORECAST and is not called one. `@fsp/projections` is the
   * engine for that and it wants a history this page does not load; naming
   * this "projected" would claim a model this page does not run.
   */
  form: z.number().nullable(),
  /** Real points in the week being shown. `null` until the lines land. */
  actual: z.number().nullable(),
  /**
   * What `@fsp/projections` expects, in this league's scoring.
   *
   * THIS IS THE FORECAST AND `form` IS NOT. The engine is `recent-form`: a
   * flat mean of the last six games played, crossing into last season when it
   * has to, with nothing at or after the projected week consulted. It scores
   * through the preset handed in, which is why a projection package that knows
   * no league rules can produce a number in ours.
   *
   * `null` when no projection was asked for.
   */
  projected: z.number().nullable(),
  /**
   * How many of this player's OWN games the projection rests on, 0 to 6.
   *
   * Zero means the engine fell back to a position average because it had
   * nothing about him — a real answer, and one a reader should be able to
   * discount. Hiding it would make a rookie's number look like a veteran's.
   */
  projectedGames: z.number().int().nonnegative(),
});
export type LabPlayer = z.infer<typeof LabPlayer>;

/** A starting slot, and who is in it. */
export const LabSlot = z.object({
  slot: z.string(),
  player: LabPlayer.nullable(),
});
export type LabSlot = z.infer<typeof LabSlot>;

export const LabTeam = z.object({
  name: z.string(),
  manager: z.string(),
  starters: z.array(LabSlot),
  bench: z.array(LabPlayer),
  /** Sum of the starters' real points, or `null` when the week has no lines. */
  total: z.number().nullable(),
  /** Sum of the starters' form. Always available; it is arithmetic on the past. */
  formTotal: z.number(),
  /** Sum of the starters' projections. `null` when none were computed. */
  projectedTotal: z.number().nullable(),
});
export type LabTeam = z.infer<typeof LabTeam>;

/**
 * How much of the week is actually in the database.
 *
 *   `final`   — the week has lines and the window is behind us.
 *   `partial` — some lines have landed. nflverse rebuilds after each game
 *               window, so a Sunday evening is legitimately half a week.
 *   `none`    — nothing published yet. The results panel says so and shows
 *               nothing, which is the whole point of having this state.
 */
export const LabWeekState = z.enum(['final', 'partial', 'none']);
export type LabWeekState = z.infer<typeof LabWeekState>;

export const LabWeekView = z.object({
  season: z.number().int(),
  week: z.number().int(),
  windowId: z.string(),
  state: LabWeekState,
  /** Which windows fed `form`, in order. Empty in week 1, and that is honest. */
  basis: z.array(z.string()),
  /** How many real stat lines this week has, and how many the basis had. */
  lineCounts: z.object({ week: z.number().int(), basis: z.number().int() }),
  /** The scoring preset the points were computed under. */
  scoring: z.string(),
  /** The projection engine's id and its own one-line description of itself. */
  projector: z.object({ id: z.string(), describes: z.string() }).nullable(),
  /** The rule that filled the rosters and the lineups, in one sentence each. */
  rules: z.object({ roster: z.string(), lineup: z.string() }),
  /** Who published the lines, and when the directory was last confirmed. */
  source: z.string(),
  asOf: z.string().nullable(),
  home: LabTeam,
  away: LabTeam,
});
export type LabWeekView = z.infer<typeof LabWeekView>;

export const LabWeekQuery = z.object({
  season: z.number().int().min(1920).max(2200),
  /** Absent means "the newest week that has any real lines". */
  week: z.number().int().min(1).max(30).nullable().default(null),
  scoring: z.string().default('ppr'),
});
export type LabWeekQuery = z.infer<typeof LabWeekQuery>;
